// Canopy's `retest_status` is a free-form string, never an enum: it can hold a value this
// codebase has never heard of. So this maps the known values to a style and falls back to
// `default` for everything else — the raw value is always displayed verbatim, never coerced
// and never allowed to throw.
const RetestStatusProps = {
  'Not Retested Yet': { bg: 'light', text: 'dark', border: 'none' },
  'Not Resolved': { bg: 'danger-subtle', text: 'danger', border: 'none' },
  'Partially Resolved': { bg: 'warning-subtle', text: 'warning-emphasis', border: 'none' },
  'Resolved': { bg: 'success-subtle', text: 'success', border: 'none' },
  'Risk Accepted': { bg: 'secondary-subtle', text: 'secondary', border: 'none' },
  'Out of Scope': { bg: 'light', text: 'dark', border: 'none' },
  default: { bg: 'light', text: 'dark', border: 'none' },
};

// Mirrors RetestRules.Normalise on the server: a blank status and "Not Retested Yet" mean the
// same thing, so they must not read differently in the UI.
export const NOT_RETESTED_YET = 'Not Retested Yet';

// Mirrors RetestStatuses.OutOfScope on the server: written onto findings the client left out of
// a requested retest. It is a statement about scope, not a tester's verdict.
export const OUT_OF_SCOPE = 'Out of Scope';

export const normaliseRetestStatus = (status?: string): string =>
  status && status.trim().length > 0 ? status : NOT_RETESTED_YET;

// A finding has a retest outcome worth showing once its status is anything other than
// "not tested yet" or "out of scope" — neither of those has a retest update behind it, so
// rendering one would show an empty section. Mirrors RetestRules.HasBeenRetested.
export const hasBeenRetested = (status?: string): boolean => {
  const normalised = normaliseRetestStatus(status);
  return normalised !== NOT_RETESTED_YET && normalised !== OUT_OF_SCOPE;
};

export const retestStatusStyle = (status?: string) =>
  RetestStatusProps[normaliseRetestStatus(status) as keyof typeof RetestStatusProps]
  ?? RetestStatusProps.default;

export default RetestStatusProps;
